import React, { useState, useCallback } from "react";
import { Button, Modal, Upload, message, Slider } from "antd";
import { UploadOutlined, EyeOutlined } from "@ant-design/icons";
import Cropper from "react-easy-crop";
import { useTranslation } from "react-i18next";
import DigitalCardPreview from "./DigitalCardPreview";

// Wallet event-ticket strip (3x). The image is cropped to exactly this size before upload.
const OUTPUT_WIDTH = 1125;
const OUTPUT_HEIGHT = 294;
const PASS_IMAGE_ASPECT = OUTPUT_WIDTH / OUTPUT_HEIGHT;
const EXAMPLE_SRC = `${process.env.PUBLIC_URL || ""}/images/digital-card-example.jpg`;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const cropToFile = async (src, area) => {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;
  canvas.getContext("2d").drawImage(img, area.x, area.y, area.width, area.height, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  return new File([blob], "digital-card.jpg", { type: "image/jpeg" });
};

/**
 * Upload + drag/zoom crop for the dedicated digital card (wallet pass) image, with an example
 * thumbnail and a live preview of the card. `onUpload(file)` must resolve to the stored image URL.
 */
const DigitalCardImageEditor = ({ value, onChange, onUpload, uploading, preview }) => {
  const { t } = useTranslation();
  const [source, setSource] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [exampleOk, setExampleOk] = useState(true);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

  const pickFile = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      message.error(t("eventForm.messages.invalidImageType"));
      return;
    }
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setSource(URL.createObjectURL(file));
  };

  const closeCropper = () => {
    if (source) URL.revokeObjectURL(source);
    setSource(null);
  };

  const apply = async () => {
    if (!area) return;
    try {
      setSaving(true);
      const url = await onUpload(await cropToFile(source, area));
      if (url) onChange(url);
      closeCropper();
    } catch (error) {
      console.error("Digital card image error:", error);
      message.error(t("eventForm.passImage.uploadFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">{t("eventForm.passImage.hint")}</p>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 180px" }}>
          <div className="text-xs text-gray-500 mb-1">{t("eventForm.passImage.yourImage")}</div>
          <div
            style={{
              width: "100%",
              aspectRatio: `${OUTPUT_WIDTH} / ${OUTPUT_HEIGHT}`,
              borderRadius: 6,
              background: value ? `url(${value}) center / cover` : "#f0f0f0",
              border: "1px dashed #d9d9d9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: "#999",
            }}
          >
            {!value && t("eventForm.passImage.noImage")}
          </div>
        </div>
        {exampleOk && (
          <div style={{ flex: "1 1 180px" }}>
            <div className="text-xs text-gray-500 mb-1">{t("eventForm.passImage.example")}</div>
            <img
              src={EXAMPLE_SRC}
              alt={t("eventForm.passImage.example")}
              onError={() => setExampleOk(false)}
              style={{ width: "100%", aspectRatio: `${OUTPUT_WIDTH} / ${OUTPUT_HEIGHT}`, objectFit: "cover", borderRadius: 6 }}
            />
          </div>
        )}
      </div>

      {/* antd wraps the upload trigger in an inline-block; make it fill its grid cell so both buttons match. */}
      <style>{`.dc-upload, .dc-upload .ant-upload { display: block !important; width: 100%; }`}</style>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginTop: 12 }}>
        <Upload className="dc-upload" accept="image/png,image/jpeg" showUploadList={false} beforeUpload={() => false} onChange={pickFile}>
          <Button block icon={<UploadOutlined />} loading={uploading}>
            {value ? t("eventForm.passImage.change") : t("eventForm.passImage.upload")}
          </Button>
        </Upload>
        <Button block icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
          {t("eventForm.passImage.previewButton")}
        </Button>
      </div>

      <Modal
        open={!!source}
        title={t("eventForm.passImage.adjustTitle")}
        onCancel={closeCropper}
        onOk={apply}
        okText={t("eventForm.passImage.apply")}
        confirmLoading={saving}
        width={640}
        destroyOnClose
      >
        <p className="text-xs text-gray-500 mb-2">{t("eventForm.passImage.adjustHint")}</p>
        <div style={{ position: "relative", height: 280, background: "#222" }}>
          {source && (
            <Cropper
              image={source}
              crop={crop}
              zoom={zoom}
              aspect={PASS_IMAGE_ASPECT}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <Slider min={1} max={3} step={0.01} value={zoom} onChange={setZoom} style={{ marginTop: 12 }} />
      </Modal>

      <Modal open={previewOpen} title={t("eventForm.passImage.previewTitle")} footer={null} onCancel={() => setPreviewOpen(false)} width={360}>
        <DigitalCardPreview {...preview} imageUrl={value} />
        <p className="text-xs text-gray-500 mt-3 text-center">{t("eventForm.passImage.previewNote")}</p>
      </Modal>
    </div>
  );
};

export default DigitalCardImageEditor;
